export const BROKER_NOTIFICATION_STATES = Object.freeze({
  ACTIVE: 'active',
  READ: 'read',
  CLEARED: 'cleared',
  ARCHIVED: 'archived',
})

export const BROKER_NOTIFICATION_POPUP_STATES = Object.freeze({
  PENDING: 'pending',
  DELIVERED: 'delivered',
  SUPPRESSED: 'suppressed',
})

const ACTIVE_STATES = new Set([
  BROKER_NOTIFICATION_STATES.ACTIVE,
  BROKER_NOTIFICATION_STATES.READ,
])
const WORKING_ORDER_NOTIFICATION_TYPES = new Set([
  'working_order_opened',
  'working_order_changed',
])
const WORKING_ORDER_GROUP_WINDOW_MS = 5 * 60 * 1000

export function isBrokerNotificationActive(record = {}) {
  return ACTIVE_STATES.has(record.notification_state)
}

export function shouldDeliverBrokerNotificationPopup(record = {}) {
  return (
    record.popup_state === BROKER_NOTIFICATION_POPUP_STATES.PENDING &&
    record.notification_state === BROKER_NOTIFICATION_STATES.ACTIVE
  )
}

export function brokerNotificationRowToUi(row = {}) {
  const details = Array.isArray(row.details)
    ? row.details
    : row.details && typeof row.details === 'object'
      ? [row.details]
      : []
  const setupSummary = setupSummaryFromDetails(details)
  const notificationState = row.notification_state || BROKER_NOTIFICATION_STATES.ACTIVE
  const popupPending = shouldDeliverBrokerNotificationPopup(row)
  const read = Boolean(row.read_at) || notificationState !== BROKER_NOTIFICATION_STATES.ACTIVE

  return {
    id: row.notification_id || row.id,
    serverBacked: true,
    brokerEventId: row.broker_event_id,
    type: row.notification_type || 'broker_event',
    tone: row.tone || 'success',
    sticky: row.sticky === true || row.tone === 'error' || row.priority === 'critical',
    symbol: row.symbol || row.instrument_key || null,
    title: row.title || 'Broker event',
    message: row.message || '',
    details,
    setupSummary,
    workspaceRoute: row.workspace_route || null,
    createdAt: row.fired_at || row.created_at || null,
    firedAt: row.fired_at || null,
    read,
    dismissed: notificationState === BROKER_NOTIFICATION_STATES.CLEARED ||
      notificationState === BROKER_NOTIFICATION_STATES.ARCHIVED,
    popupDismissed: !popupPending,
    notificationState,
    popupState: row.popup_state || BROKER_NOTIFICATION_POPUP_STATES.DELIVERED,
  }
}

function timestampMs(notification = {}) {
  const date = new Date(notification.firedAt || notification.createdAt || 0)
  const value = date.getTime()
  return Number.isFinite(value) ? value : 0
}

function workingOrderGroupKey(notification = {}) {
  if (
    notification.serverBacked !== true ||
    !WORKING_ORDER_NOTIFICATION_TYPES.has(notification.type) ||
    !notification.symbol
  ) {
    return null
  }
  const bucket = Math.floor(timestampMs(notification) / WORKING_ORDER_GROUP_WINDOW_MS)
  return `broker-working-orders:${notification.symbol}:${bucket}`
}

function detailKey(row = {}) {
  return [
    row.accountNumber,
    row.orderId,
    row.role,
    row.status,
    row.summary,
  ].map(value => String(value ?? '').trim()).join(':')
}

function setupSummaryScore(summary = {}) {
  const accounts = Array.isArray(summary.accounts) ? summary.accounts.length : 0
  const shares = Number(summary.totalShares) || 0
  const risk = Number(summary.maxRisk) || 0
  const hasProject = summary.projectId ? 1 : 0
  return hasProject * 1_000_000 + accounts * 10_000 + shares * 100 + risk
}

function setupSummaryFromDetails(details = []) {
  const summaries = []
  for (const row of Array.isArray(details) ? details : []) {
    if (row?.setupSummary && typeof row.setupSummary === 'object') summaries.push(row.setupSummary)
  }
  return summaries.sort((a, b) => setupSummaryScore(b) - setupSummaryScore(a))[0] || null
}

function setupSummaryFromNotifications(items = [], details = []) {
  const summaries = [
    ...items.map(item => item?.setupSummary).filter(Boolean),
    setupSummaryFromDetails(details),
  ].filter(Boolean)
  return summaries.sort((a, b) => setupSummaryScore(b) - setupSummaryScore(a))[0] || null
}

function collectGroupedDetails(items = []) {
  const byKey = new Map()
  for (const item of [...items].sort((a, b) => timestampMs(a) - timestampMs(b))) {
    for (const row of Array.isArray(item.details) ? item.details : []) {
      const key = detailKey(row)
      if (!key.replaceAll(':', '')) continue
      byKey.set(key, row)
    }
  }
  return [...byKey.values()].sort((a, b) => {
    const accountCompare = String(a.accountNumber || '').localeCompare(String(b.accountNumber || ''))
    if (accountCompare) return accountCompare
    return String(a.role || '').localeCompare(String(b.role || ''))
  })
}

function buildWorkingOrderGroup(groupId, items = []) {
  const sorted = [...items].sort((a, b) => timestampMs(b) - timestampMs(a))
  const latest = sorted[0] || {}
  const details = collectGroupedDetails(items)
  const setupSummary = setupSummaryFromNotifications(sorted, details)
  const sourceNotificationIds = sorted.map(item => item.id).filter(Boolean)
  const sourceBrokerEventIds = sorted.map(item => item.brokerEventId).filter(Boolean)
  const accountCount = new Set(
    details.map(row => String(row.accountNumber || '').trim()).filter(Boolean)
  ).size
  const orderCount = details.length || sourceNotificationIds.length
  const allRead = sorted.every(item => item.read === true)
  const allDismissed = sorted.every(item => item.dismissed === true)
  const allPopupDismissed = sorted.every(item => item.popupDismissed === true)
  const notificationState = allDismissed
    ? BROKER_NOTIFICATION_STATES.CLEARED
    : allRead
      ? BROKER_NOTIFICATION_STATES.READ
      : BROKER_NOTIFICATION_STATES.ACTIVE

  return {
    id: groupId,
    serverBacked: true,
    grouped: true,
    groupType: 'broker-working-orders',
    sourceNotificationIds,
    sourceBrokerEventIds,
    type: 'working_order_group',
    tone: latest.tone || 'info',
    sticky: sorted.some(item => item.sticky === true),
    symbol: latest.symbol,
    title: `${latest.symbol} orders working`,
    message: [
      `${orderCount} ${orderCount === 1 ? 'order' : 'orders'} working`,
      accountCount ? `${accountCount} ${accountCount === 1 ? 'account' : 'accounts'}` : null,
    ].filter(Boolean).join(' · '),
    details,
    setupSummary,
    workspaceRoute: latest.workspaceRoute || null,
    createdAt: latest.createdAt || latest.firedAt || null,
    firedAt: latest.firedAt || latest.createdAt || null,
    read: allRead,
    dismissed: allDismissed,
    popupDismissed: allPopupDismissed,
    notificationState,
    popupState: allPopupDismissed
      ? BROKER_NOTIFICATION_POPUP_STATES.DELIVERED
      : BROKER_NOTIFICATION_POPUP_STATES.PENDING,
  }
}

export function groupNotificationsForDisplay(notifications = []) {
  const passthrough = []
  const groups = new Map()

  for (const notification of notifications) {
    const groupId = workingOrderGroupKey(notification)
    if (!groupId) {
      passthrough.push(notification)
      continue
    }
    const group = groups.get(groupId) || []
    group.push(notification)
    groups.set(groupId, group)
  }

  for (const [groupId, items] of groups.entries()) {
    if (items.length === 1) {
      passthrough.push(items[0])
    } else {
      passthrough.push(buildWorkingOrderGroup(groupId, items))
    }
  }

  return passthrough
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))
    .slice(0, 50)
}

export function mergeNotifications(existing = [], incoming = []) {
  const byId = new Map()
  for (const item of [...incoming, ...existing]) {
    if (item?.id && !byId.has(item.id)) byId.set(item.id, item)
  }
  return [...byId.values()]
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))
    .slice(0, 50)
}
